import qs from 'qs';
import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import { resolvePermissionCheck } from '@/utils/groupPermissions';
import { executeBulkAction as executeBulk } from '@/utils/bulk';
import { withSelectedIds } from '@/utils/selectionStats';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { limit: 20 };

/** Concurrent identical invoice list fetches share one promise. */
let inflightFetchInvoices = null;

const createState = (options = {}) => ({
    url: null,
    invoices: [],
    invoices_count: null,
    invoices_loading: false,
    invoices_loaded: false,
    invoices_loading_errors: null,
    invoices_filters: { offset: 0, ...DEFAULT_FILTERS },
    cancelTokenSource: null,

    selected_items: [],
    bulk_action_loading: false,

    fetch_stats: false,
    invoices_stats: {},
    invoices_stats_loading: false,
    invoices_stats_loading_errors: null,
    invoices_stats_cancel_source: null,

    _storageOptions: options || {},
});


const mutations = {

    updateInvoices(state, invoices) {
        state.invoices = invoices;
    },
    updateInvoicesCount(state, count) {
        state.invoices_count = count;
    },
    updateInvoicesLoading(state, loading) {
        state.invoices_loading = loading;
    },
    updateInvoicesLoaded(state, loaded) {
        state.invoices_loaded = loaded;
    },
    updateInvoicesLoadingErrors(state, errors) {
        state.invoices_loading_errors = errors;
    },
    updateInvoicesFilters(state, filters) {
        state.invoices_filters = filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.invoices_filters, fieldsToSave);
        }
    },
    updateUrl(state, url) {
        state.url = url;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
    updateSelectedItems(state, selected_items) {
        state.selected_items = selected_items;
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },
    updateFetchStats(state, fetch_stats) {
        state.fetch_stats = fetch_stats;
    },
    updateInvoicesStats(state, invoices_stats) {
        state.invoices_stats = invoices_stats;
    },
    updateInvoicesStatsLoading(state, loading) {
        state.invoices_stats_loading = loading;
    },
    updateInvoicesStatsLoadingErrors(state, errors) {
        state.invoices_stats_loading_errors = errors;
    },
    updateInvoicesStatsCancelSource(state, source) {
        state.invoices_stats_cancel_source = source;
    },
};

const actions = {

    async deleteInvoice({ commit, dispatch, state }, invoice) {
        try {
            const response = await axios.delete(`${state.url}${invoice.id}/`);
            dispatch('fetchInvoicesStats');
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        }
    },

    async markInvoiceAsPaid({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(
                `${state.url}${params.invoice.id}/paid/`,
                { paid: true }
            );
            dispatch('fetchInvoicesStats');
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit('updateBulkActionLoading', true);
        try {
            const result = await executeBulk({
                bulkActionUrl: `${state.url}bulk/`,
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit('updateSelectedItems', []);
            dispatch('fetchInvoices');
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateBulkActionLoading', false);
        }
    },

    async fetchInvoices({ commit, dispatch, state, rootGetters }, params) {
        const permissions = rootGetters['session/current_user_permissions'];
        if (!resolvePermissionCheck(permissions, 'billing.view_invoice')) {
            commit('updateInvoicesLoaded', true);
            commit('updateInvoicesCount', 0);
            commit('updateInvoices', []);
            return;
        }

        if (!state.url) {
            return;
        }

        const force = Boolean(params && params.force);
        if (inflightFetchInvoices) {
            if (!force) {
                return inflightFetchInvoices;
            }
            try {
                await inflightFetchInvoices;
            } catch {
                /* stale in-flight fetch; start a new one below */
            }
        }

        inflightFetchInvoices = (async () => {
            // Cancel any pending request (superseded by this one)
            if (state.cancelTokenSource) {
                state.cancelTokenSource.cancel("New fetch started");
            }

            const source = axios.CancelToken.source();
            commit('updateCancelTokenSource', source);

            commit('updateInvoicesLoading', true);
            commit('updateInvoicesLoadingErrors', null);
            if (state.fetch_stats) {
                commit('updateInvoicesStatsLoading', true);
            }

            try {
                const response = await axios.get(state.url, {
                    params: state.invoices_filters,
                    cancelToken: source.token,
                });
                commit('updateInvoices', response.data.results);
                commit(
                    'updateInvoicesCount',
                    response.data.count ?? response.data.results?.length ?? 0
                );
                commit('updateInvoicesLoaded', true);
                dispatch('fetchInvoicesStats');
                return response;
            } catch (xhr_error) {
                if (axios.isCancel(xhr_error)) {
                    return;
                }
                const error = utils.handleError(xhr_error);
                commit('updateInvoicesLoadingErrors', error.details);
                commit('updateInvoicesLoaded', true);
                throw error;
            } finally {
                commit('updateInvoicesLoading', false);
                inflightFetchInvoices = null;
            }
        })();

        return inflightFetchInvoices;
    },

    async fetchInvoicesStats({ commit, state, rootGetters }) {
        if (!state.fetch_stats) {
            commit('updateInvoicesStatsLoading', false);
            return;
        }
        if (!state.url) {
            commit('updateInvoicesStatsLoading', false);
            return;
        }
        if (!resolvePermissionCheck(rootGetters['session/current_user_permissions'], 'billing.view_invoice')) {
            commit('updateInvoicesStatsLoading', false);
            return;
        }

        if (state.invoices_stats_cancel_source) {
            state.invoices_stats_cancel_source.cancel("New stats fetch started");
        }

        const source = axios.CancelToken.source();
        commit('updateInvoicesStatsCancelSource', source);

        commit('updateInvoicesStatsLoading', true);
        commit('updateInvoicesStatsLoadingErrors', null);

        try {
            const response = await axios.get(`${state.url}stats/`, {
                params: withSelectedIds(state.invoices_filters, state.selected_items),
                cancelToken: source.token,
            });
            commit('updateInvoicesStats', response.data);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateInvoicesStatsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateInvoicesStatsLoading', false);
        }
    },

    async init({ commit, state }, params) {
        if (!params.url) {
            throw new Error("Missing required parameter: url");
        }

        await commit('updateUrl', params.url);
        await commit('updateInvoices', []);
        await commit('updateInvoicesCount', null);
        await commit('updateInvoicesLoaded', false);
        await commit('updateSelectedItems', []);
        await commit('updateInvoicesStats', {});
        await commit('updateFetchStats', params.fetch_stats === undefined ? false : params.fetch_stats);

        if (params.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateInvoicesFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(
                state._storageOptions?.storageKey,
                {},
                state._storageOptions?.fieldsToExclude,
            ) : {}),
            ...(params.filters || {})
        });
    },

};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});